// BankManagement.js - Enhanced with Invitation Workflow & Relationship Parameters
// Super Admin component for managing bank partners

import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Search, Edit, Ban, Trash2, Eye, RefreshCw, X, CheckCircle,
  Clock, AlertTriangle, DollarSign, Users, TrendingUp, Phone, Mail, MapPin,
  Shield, Send, Percent, ArrowRight, Copy, ExternalLink
} from 'lucide-react';
import { invitationsApi } from '../../../services/invitationsApi';
import { getStatusStyle } from '../../../config/statusColors';

// Country options for bank registration
const COUNTRIES = [
  { code: 'KE', name: 'Kenya', currency: 'KES' },
  { code: 'TZ', name: 'Tanzania', currency: 'TZS' },
  { code: 'UG', name: 'Uganda', currency: 'UGX' },
  { code: 'RW', name: 'Rwanda', currency: 'RWF' },
  { code: 'ET', name: 'Ethiopia', currency: 'ETB' },
  { code: 'GH', name: 'Ghana', currency: 'GHS' },
  { code: 'NG', name: 'Nigeria', currency: 'NGN' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR' },
];

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClasses = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-5xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl ${sizeClasses[size]} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    purple: 'bg-gray-100 text-gray-600',
    green: 'bg-gray-100 text-gray-600',
    blue: 'bg-gray-100 text-gray-600',
    amber: 'bg-gray-100 text-gray-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

const BankManagement = () => {
  const [banks, setBanks] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('banks'); // 'banks' | 'invitations'
  
  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organizationName: '',
    country: 'Kenya',
    city: '',
    // Relationship parameters
    fxTransactionFee: 1.0,
    forwardsContractFee: 1.0,
    defaultProtectionFee: 1.0,
    mobileLoansOriginationFee: 1.0,
    prudentialReserveRatio: 10.0,
  });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load invitations
      const invitationsData = await invitationsApi.list({ type: 'tenant_bank' });
      setInvitations(invitationsData);
      
      // TODO: Load actual banks from tenants API
      // const banksData = await tenantsApi.list({ type: 'bank' });
      // setBanks(banksData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteBank = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    try {
      const result = await invitationsApi.inviteBank(formData);
      
      setInviteSuccess({
        email: result.email,
        organizationName: result.organizationName,
        emailSent: result.emailSent,
        tempPassword: result.tempPassword, // Only in dev mode
      });
      
      // Reload invitations
      loadData();
      
      // Reset form
      setFormData({
        firstName: '', lastName: '', email: '', organizationName: '',
        country: 'Kenya', city: '',
        fxTransactionFee: 1.0, forwardsContractFee: 1.0, defaultProtectionFee: 1.0,
        mobileLoansOriginationFee: 1.0, prudentialReserveRatio: 10.0,
      });
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (id) => {
    try {
      await invitationsApi.resend(id);
      loadData();
    } catch (error) {
      alert('Failed to resend invitation: ' + error.message);
    }
  };

  const handleCancelInvitation = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;
    try {
      await invitationsApi.cancel(id);
      loadData();
    } catch (error) {
      alert('Failed to cancel invitation: ' + error.message);
    }
  };


  const activeBanks = banks.filter(b => b.status === 'active').length;
  const pendingInvitations = invitations.filter(i => ['pending', 'email_sent'].includes(i.status)).length;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bank Management</h1>
            <p className="text-gray-600 mt-1">Manage partner banks, onboarding, and relationship parameters</p>
          </div>
          <button
            onClick={() => { setShowInviteModal(true); setInviteSuccess(null); }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
            Invite Bank
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Banks" value={banks.length} icon={Building2} color="purple" subtitle={`${activeBanks} active`} />
        <StatCard title="Pending Invitations" value={pendingInvitations} icon={Clock} color="amber" />
        <StatCard title="Active Banks" value={activeBanks} icon={CheckCircle} color="green" />
        <StatCard title="Total Invitations" value={invitations.length} icon={Send} color="blue" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('banks')}
          className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'banks' ? 'bg-gray-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
        >
          Banks ({banks.length})
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-4 py-2 rounded-lg font-medium ${activeTab === 'invitations' ? 'bg-gray-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
        >
          Invitations ({invitations.length})
        </button>
      </div>

      {/* Invitations Table */}
      {activeTab === 'invitations' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">Bank Invitations</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invitations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No invitations yet. Click "Invite Bank" to get started.
                    </td>
                  </tr>
                ) : (
                  invitations.map(inv => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{inv.organization_name}</p>
                            <p className="text-xs text-gray-500">{inv.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-gray-900">{inv.recipient_name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium" style={getStatusStyle(inv.status)}>
                          {inv.status_label || inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600">{inv.inviter_name}</td>
                      <td className="px-4 py-4 text-gray-600">
                        {new Date(inv.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {['pending', 'email_sent'].includes(inv.status) && (
                            <>
                              <button
                                onClick={() => handleResendInvitation(inv.id)}
                                className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg"
                                title="Resend"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCancelInvitation(inv.id)}
                                className="p-1.5 text-gray-600 hover:bg-gray-50 rounded-lg"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Banks Table (placeholder) */}
      {activeTab === 'banks' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-500">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>Banks will appear here once invitations are confirmed.</p>
          <button
            onClick={() => setActiveTab('invitations')}
            className="mt-4 text-gray-600 hover:underline"
          >
            View Invitations →
          </button>
        </div>
      )}

      {/* Invite Bank Modal */}
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Invite New Bank Partner" size="lg">
        {inviteSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-gray-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Invitation Sent!</h3>
            <p className="text-gray-600 mb-4">
              An invitation email has been sent to <strong>{inviteSuccess.email}</strong> for{' '}
              <strong>{inviteSuccess.organizationName}</strong>.
            </p>
            {inviteSuccess.tempPassword && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm text-gray-800 font-medium mb-2">🔧 Development Mode - Demo Credentials:</p>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <code className="text-sm">{inviteSuccess.tempPassword}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(inviteSuccess.tempPassword)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowInviteModal(false); setInviteSuccess(null); }}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => setInviteSuccess(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Invite Another
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleInviteBank} className="space-y-6">
            {formError && (
              <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded-lg">
                {formError}
              </div>
            )}

            {/* Contact Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Bank Admin Contact
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    placeholder="Kamau"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="admin@bank.co.ke"
                />
              </div>
            </div>

            {/* Organization Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Bank Information
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.organizationName}
                    onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    placeholder="Equity Bank Kenya"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
                    <select
                      required
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                      placeholder="Nairobi"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Relationship Parameters */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Relationship Parameters (Fees & Revenue Share)
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Foreign Exchange Transaction Fee (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={formData.fxTransactionFee}
                      onChange={(e) => setFormData({ ...formData, fxTransactionFee: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Fee on foreign exchange conversions</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Forwards Contract Fee (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={formData.forwardsContractFee}
                      onChange={(e) => setFormData({ ...formData, forwardsContractFee: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Fee on hedging contracts</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Protection Fee (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={formData.defaultProtectionFee}
                      onChange={(e) => setFormData({ ...formData, defaultProtectionFee: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Insurance against defaults</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Settlement Fee (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      value={formData.mobileLoansOriginationFee}
                      onChange={(e) => setFormData({ ...formData, mobileLoansOriginationFee: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Per-transaction settlement fee</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prudential Reserve Ratio (%)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="5"
                      max="25"
                      value={formData.prudentialReserveRatio}
                      onChange={(e) => setFormData({ ...formData, prudentialReserveRatio: parseFloat(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Bank's prudential reserve ratio</p>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Fee Summary Preview</h5>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• On a $1M investment at {formData.prudentialReserveRatio}% reserve ratio → <strong>{(1000000 * (100 / formData.prudentialReserveRatio)).toLocaleString()} KES</strong> deployable M2</p>
                    <p>• <span className="text-gray-900">Forwards</span><span className="text-primary-600">flow</span> fees on transaction: <strong>{(formData.fxTransactionFee + formData.forwardsContractFee + formData.defaultProtectionFee).toFixed(1)}%</strong></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default BankManagement;
